import type { IProduct } from '@/Interfaces/Product.interface';
import DateRangeWithShortcuts from '@/components/DateRangeWithShortcuts';
import BigCalendarIcon from '@/components/Icons/BigCalendarIcon';
import LoadingBackdropWrapper from '@/components/Modal/common/LoadingBackdropWrapper';
import ModalBody from '@/components/Modal/common/ModalBody';
import ModalTitleCard from '@/components/Modal/common/ModalTitleCard';
import NumberInput from '@/components/NumberInput';
import { dv360lineItemsFormToApiDuplicate } from '@/helpers/dv360Tickets/campaignManagement/lineItemForm';
import { defaultDuplicationFormData } from '@/pages/CampaignManagement/CreateCampaign/DV360TicketsLineItemForm/constants';
import { duplicateDv360TicketsLineItemFormValidationSchema } from '@/pages/CampaignManagement/CreateCampaign/DV360TicketsLineItemForm/validationSchema';
import { duplicateDV360TicketsLineItem } from '@/store/api/dv360Tickets/lineItems.api';
import { useAppSelector } from '@/store/hooks';
import { selectDCV360TicketsProducts } from '@/store/selectors/dv360Tickets/insertionOrders.selectors';
import { yupResolver } from '@hookform/resolvers/yup';
import { Autocomplete, Button, Modal, TextField } from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import { isAxiosError } from 'axios';
import { Moment } from 'moment/moment';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, FormProvider, useForm, useWatch } from 'react-hook-form';
import { toast } from 'react-toastify';

interface IProps {
  isOpen: boolean;
  lineItemId: number;
  product: IProduct;
  className?: string;
  handleClose: (refresh?: boolean) => void;
}

const DuplicateDV360TicketsLineItemModal = ({ isOpen, className, lineItemId, product, handleClose }: IProps) => {
  const [loading, setLoading] = useState(false);

  const { products } = useAppSelector(selectDCV360TicketsProducts);

  const resolver = useMemo(() => yupResolver(duplicateDv360TicketsLineItemFormValidationSchema), []);

  const form = useForm({
    resolver,
    // @ts-ignore
    defaultValues: { ...defaultDuplicationFormData, product },
    mode: 'onChange',
  });

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    formState: { isValid },
  } = form;

  // types
  const [watchStartDate, watchEndDate] = useWatch({
    control,
    name: ['startDate', 'endDate'],
  });

  useEffect(() => {
    if (isOpen) {
      // @ts-ignore
      reset({ ...defaultDuplicationFormData, product });
    }
  }, [isOpen, reset, product]);

  const handleSetDuplicateDates = useCallback(
    ([startDate, endDate]: DateRange<Moment>) => {
      setValue('startDate', startDate, {
        shouldValidate: true,
        shouldDirty: true,
      });
      setValue('endDate', endDate, {
        shouldValidate: true,
        shouldDirty: true,
      });
    },
    [setValue],
  );

  const onSubmit = useCallback(
    async (data: any) => {
      if (!lineItemId) return;

      const payload = dv360lineItemsFormToApiDuplicate(data);

      setLoading(true);
      try {
        const { id } = await duplicateDV360TicketsLineItem(lineItemId, payload);
        reset();
        handleClose(!!id);
      } catch (error) {
        if (isAxiosError(error)) {
          toast.error(error?.response?.data?.message || 'Unknown Internal Error');
        } else {
          toast.error((error as Error).message || 'Something went wrong');
        }
      } finally {
        setLoading(false);
      }
    },
    [lineItemId, reset, handleClose, setLoading],
  );

  if (!lineItemId) {
    return null;
  }

  return (
    <LoadingBackdropWrapper backdropOpen={loading}>
      <Modal open={isOpen}>
        <ModalBody
          sx={{ width: '1200px', minHeight: '320px', display: 'flex', flexDirection: 'column', overflow: 'visible' }}
          className={className}
        >
          <div>
            <ModalTitleCard titleIcon={<BigCalendarIcon />} handleClose={handleClose} title='Line Item Duplication' />
            <FormProvider {...form}>
              <form className='mt-4' onSubmit={handleSubmit(onSubmit)}>
                <div className='flex flex-row justify-between gap-1 mb-5'>
                  <div className='w-4/12 h-[48px]'>
                    <DateRangeWithShortcuts
                      value={[watchStartDate as Moment | null, watchEndDate as Moment | null]}
                      onChange={range => handleSetDuplicateDates(range)}
                      timezone='UTC'
                      disablePast
                      inputProps={{
                        slotProps: {
                          input: {
                            startAdornment: null,
                            placeholder: 'Select Date Range',
                            sx: { '& input': { paddingRight: 0 } },
                          },
                        },
                      }}
                    />
                  </div>
                  <div className='w-4/12'>
                    <Controller
                      name='product'
                      control={control}
                      render={({ field }) => (
                        <Autocomplete<IProduct, false, true>
                          value={field.value as IProduct}
                          options={products || []}
                          isOptionEqualToValue={(option, value) => option.id === value?.id}
                          getOptionKey={option => option.id}
                          getOptionLabel={option => option.name}
                          onChange={(_, data) => field.onChange(data)}
                          sx={{ height: '48px' }}
                          fullWidth
                          renderInput={params => (
                            <TextField {...params} placeholder='Select Product' variant='outlined' />
                          )}
                          disableClearable
                        />
                      )}
                    />
                  </div>
                  <div className='w-4/12'>
                    <Controller
                      name='name'
                      control={control}
                      render={({ field }) => (
                        <TextField {...field} sx={{ height: '48px' }} fullWidth placeholder='Line Item Name' />
                      )}
                    />
                  </div>
                  <div className='w-4/12'>
                    <Controller
                      name='budget'
                      control={control}
                      render={({ field }) => (
                        <NumberInput
                          {...field}
                          //@ts-expect-error js component
                          fullWidth
                          placeholder='Wholesale Budget'
                          disableInputArrows
                          maximumFractionDigits={2}
                          NumberInputProps={{
                            thousandsSeparator: ',',
                            unmask: true,
                          }}
                        />
                      )}
                    />
                  </div>
                </div>
                <div className='mt-10 flex justify-end gap-[12px]'>
                  <Button color='inherit' variant='outlined' onClick={() => handleClose()}>
                    Cancel
                  </Button>
                  <Button color='primary' variant='contained' type='submit' disabled={!isValid}>
                    Duplicate
                  </Button>
                </div>
              </form>
            </FormProvider>
          </div>
        </ModalBody>
      </Modal>
    </LoadingBackdropWrapper>
  );
};

export default DuplicateDV360TicketsLineItemModal;
